import React, { useState } from "react";
import { FaArrowRight, FaDesktop, FaChartLine, FaShieldHalved } from "react-icons/fa6";
import { Container } from "../home/ui";

// Hero artwork (laptop + phone mockup). On desktop it sits as a background on the right side
// of the section; on mobile/tablet it shows below the text. Falls back to the home hero art.
const heroImg = "/img/website-dev-hero.jpg";
const fallbackImg = "/img/hero-bg1.jpg";

const features = [
  { icon: FaDesktop, title: "Modern & Responsive", sub: "Designs", tone: "bg-[#E8F1FF] text-[#1E6BFF]" },
  { icon: FaChartLine, title: "SEO Friendly", sub: "Architecture", tone: "bg-[#E8F1FF] text-[#1E6BFF]" },
  { icon: FaShieldHalved, title: "Fast & Secure", sub: "Websites", tone: "bg-[#E8F1FF] text-[#1E6BFF]" },
];

// Soft edges so the artwork melts into the section background.
const edgeFade = {
  WebkitMaskImage: "radial-gradient(ellipse 78% 80% at 52% 50%, #000 66%, transparent 100%)",
  maskImage: "radial-gradient(ellipse 78% 80% at 52% 50%, #000 66%, transparent 100%)",
};

const WebHero = () => {
  const [src, setSrc] = useState(heroImg);

  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-[#FCFCFE] via-[#F8FAFF] to-[#F3F7FE] pb-[48px] pt-[110px] xl:flex xl:min-h-[min(40vw,760px)] xl:items-center xl:pb-[40px] xl:pt-[calc(clamp(76px,5.83vw,112px)+24px)]">
      {/* Desktop: artwork as background on the right, natural ratio (never stretched or cropped) */}
      <img
        src={src}
        alt=""
        aria-hidden="true"
        onError={() => src !== fallbackImg && setSrc(fallbackImg)}
        className="pointer-events-none absolute right-[1vw] top-[calc(50%+clamp(38px,2.9vw,56px))] hidden h-[min(30vw,580px)] w-auto max-w-none -translate-y-1/2 select-none xl:block"
        style={edgeFade}
      />

      <Container className="relative grid max-w-[1900px] items-center gap-[32px] xl:grid-cols-[44%_1fr] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
        <div>
          <span className="inline-flex items-center gap-[8px] rounded-full bg-[#E9F0FF] px-[16px] py-[7px] text-[12px] font-medium uppercase tracking-[0.12em] text-[#1E6BFF]">
            <span className="h-[5px] w-[5px] rounded-full bg-[#1E6BFF]" />
            Website Development
          </span>

          <h1 className="mt-[20px] text-[38px] font-bold leading-[1.12] tracking-tight text-ink sm:text-[52px] xl:text-[clamp(40px,3.2vw,62px)]">
            Powerful Websites
            <br />
            <span className="text-gradient">for Growing Businesses.</span>
          </h1>

          <p className="mt-[20px] max-w-[640px] text-[16px] leading-[1.65] text-slate-600 sm:text-[17px] xl:text-[clamp(16px,1.05vw,19px)]">
            We design and develop modern, responsive, and high-performing websites that help you build your brand,
            attract more customers, and grow your business online.
          </p>

          <div className="mt-[30px] flex flex-wrap items-center gap-[16px]">
            <a
              href="/contact-us"
              className="group inline-flex h-[54px] items-center gap-[10px] rounded-full bg-gradient-to-r from-[#1E4DFF] via-[#5B3DF5] to-[#A21CF0] px-[28px] text-[16px] font-medium text-white shadow-[0_14px_30px_-14px_rgba(91,61,245,0.7)] transition hover:brightness-110"
            >
              Get a Free Consultation
              <FaArrowRight className="text-[14px] transition group-hover:translate-x-1" />
            </a>
            <a
              href="/our-completed-projects"
              className="inline-flex h-[54px] items-center rounded-full border-[1.5px] border-solid border-[#7FA3FF] bg-white/70 px-[32px] text-[16px] font-medium text-[#1E4DFF] transition hover:bg-[#EEF3FF]"
            >
              View Our Work
            </a>
          </div>

          <div className="mt-[36px] grid max-w-[620px] grid-cols-1 gap-[16px] sm:grid-cols-3 sm:gap-[12px]">
            {features.map((f) => (
              <div key={f.title} className="flex items-center gap-[12px]">
                <span className={`inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-[12px] text-[18px] ${f.tone}`}>
                  <f.icon />
                </span>
                <span className="text-[14px] leading-[1.4] text-slate-500">
                  <span className="block font-semibold text-ink">{f.title}</span>
                  {f.sub}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile / tablet: artwork below the text */}
        <div className="flex justify-center xl:hidden">
          <img
            src={src}
            onError={() => src !== fallbackImg && setSrc(fallbackImg)}
            alt="Responsive website shown on a laptop and a phone"
            className="h-auto w-full max-w-[760px] select-none"
            style={edgeFade}
          />
        </div>
      </Container>
    </section>
  );
};

export default WebHero;
